import styles from './ProductsSummarySection.module.scss'
import { Card, Container, Heading, Shape, Switch, Tag, Toggle } from '@boltenergy-be/design-system'
import {
  CUSTOMER_TYPES,
  CustomerType,
  EL_PRODUCT_CODES,
  GAS_PRODUCT_CODES,
  Product,
  PRODUCT_CONFIG_OPTIONS,
  PRODUCTS_CONFIG,
  Region,
  REGIONS
} from '@boltenergy-be/shared'
import ProductDrawer from './components/product-drawer/ProductDrawer'
import { KeyboardEvent, useEffect, useEffectEvent, useRef, useState } from 'react'
import { BOLT_PRICING_PRODUCTS, PLENTY_PRICING_PRODUCTS } from './components/products-summary/constants'
import { ProductsSummarySectionProps } from './types'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import { getUsableProducts } from 'utils/products'
import ProductsSummary from './components/products-summary/ProductsSummary'
import { SELECTABLE_CATEGORIES, SelectableCategory } from './constants'
import classNames from 'classnames'
import { PLENTY_URL } from 'constants/constants'
import Link from 'components/link/Link'
import ReviewRatings from 'components/review-ratings/ReviewRatings'
import parse from 'html-react-parser'

const ProductsSummarySection = ({
  apiProducts,
  cmsProducts,
  extraStartSimulationAction,
  platformCostReduction
}: ProductsSummarySectionProps) => {
  // React Router
  const { locale, query } = useRouter()
  const { tab } = query

  // i18n
  const { t } = useTranslation(['pricing', 'common'])

  // Local state
  const [currentCategory, setCurrentCategory] = useState<SelectableCategory>(SELECTABLE_CATEGORIES.bolt)
  const [currentRegion, setCurrentRegion] = useState<Region>(REGIONS.flanders)
  const [customerType, setCustomerType] = useState<CustomerType>(CUSTOMER_TYPES.residential)
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState<boolean>(false)
  const [drawerProduct, setDrawerProduct] = useState<Product | null>(null)

  // Reference
  const tabsRef = useRef<HTMLButtonElement[]>([])
  const tabsContentRef = useRef<HTMLDivElement[]>([])

  // Constants
  const categoryProducts =
    currentCategory === 'plenty' || currentRegion !== REGIONS.flanders ? PLENTY_PRICING_PRODUCTS : BOLT_PRICING_PRODUCTS
  const usableProducts = getUsableProducts({
    withPlenty: currentCategory === 'plenty',
    region: currentRegion,
    isEarlyBird: false
  })
  const visibleUsableProducts = categoryProducts.filter((product) => usableProducts.includes(product))
  const showTabs = currentRegion === REGIONS.flanders

  // Set default category to "Plenty" if anchor tab=plenty is present
  const setCurrentCategoryEvent = useEffectEvent(setCurrentCategory)
  useEffect(() => {
    if (tab && typeof tab === 'string' && tab.toLowerCase() === 'plenty') {
      setCurrentCategoryEvent(SELECTABLE_CATEGORIES.plenty)
    }
  }, [tab])

  /**
   * Handles keyboard navigation for the tab list to make it accessible
   * @param {KeyboardEvent<HTMLDivElement>} e
   */
  const handleTabListKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const currentTab = e.target
    const tabs = tabsRef.current
    const currentIndex = tabs.indexOf(currentTab as HTMLButtonElement)
    const currentContent = tabsContentRef.current[currentIndex]

    if (currentIndex === -1) return // Exit if the focused element is not a tab
    let newIndex = 0

    switch (e.key) {
      case 'ArrowRight':
        newIndex = (currentIndex + 1) % tabs.length
        tabs[newIndex].focus()
        break
      case 'ArrowLeft':
        newIndex = (currentIndex - 1 + tabs.length) % tabs.length
        tabs[newIndex].focus()
        break
      case 'Home':
        tabs[0].focus()
        break
      case 'End':
        tabs[tabs.length - 1].focus()
        break
      case 'ArrowDown':
        currentContent.focus()
        break
      default:
        return // Exit if the key is not recognized
    }

    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <>
      <Container as="section" color="accent-vibrant" classnames={{ content: styles.products, container: styles['products-container'] }}>
        <header className={styles.heading}>
          <ReviewRatings show={{ googleReviews: true, trustpilot: true }} />
          <Heading as="h2" variant="d3" className={styles.title}>
            {t('comparison.title')}
          </Heading>
        </header>

        <div className={styles['summary-and-filters']}>
          <div className={styles.filters}>
            <Toggle
              active={currentRegion}
              id="region"
              onClick={(value: Region) => setCurrentRegion(value)}
              options={[
                { label: t(`common:regions.${REGIONS.flanders.toLowerCase()}`), value: REGIONS.flanders },
                { label: t(`common:regions.${REGIONS.brussels.toLowerCase()}`), value: REGIONS.brussels },
                { label: t(`common:regions.${REGIONS.wallonia.toLowerCase()}`), value: REGIONS.wallonia }
              ]}
              color="contrast"
              size="lg"
              radius="full"
              className={styles.toggle}
              optionClassName={styles['toggle-option']}
            />

            <Switch
              label={t('comparison.toggle.label')}
              checked={customerType === CUSTOMER_TYPES.professional}
              className={styles.switch}
              sliderClassName={styles['switch-slider']}
              onChange={() =>
                setCustomerType(customerType === CUSTOMER_TYPES.residential ? CUSTOMER_TYPES.professional : CUSTOMER_TYPES.residential)
              }
            />
          </div>

          <div className={styles.tabs}>
            {showTabs && (
              <div role="tablist" className={styles['tab-list']} onKeyDown={handleTabListKeyDown}>
                {Object.values(SELECTABLE_CATEGORIES).map((category, index) => (
                  <button
                    key={`tab-${category}`}
                    role="tab"
                    aria-selected={currentCategory === category}
                    aria-controls={`content-${category}`}
                    id={`tab-${category}`}
                    tabIndex={currentCategory === category ? 0 : -1}
                    onClick={() => setCurrentCategory(category)}
                    ref={(element) => {
                      tabsRef.current[index] = element
                    }}
                    className={styles.tab}
                  >
                    <strong className="h4">{t(`comparison.tabs.${category}.title`)}</strong>

                    {category === SELECTABLE_CATEGORIES.plenty && (
                      <Tag type="warm" size="small" className={styles['plenty-tag']}>
                        {t('comparison.tabs.plenty.tag')}
                      </Tag>
                    )}

                    <small className={classNames('h6 serif', styles['tab-description'])}>
                      {t(`comparison.tabs.${category}.description`)}
                    </small>
                  </button>
                ))}
              </div>
            )}

            <Card
              data-count={visibleUsableProducts.length}
              className={classNames(styles['tab-card'], { [styles['round-all']]: !showTabs })}
              border={false}
            >
              {showTabs ? (
                Object.values(SELECTABLE_CATEGORIES).map((category, index) => (
                  <div
                    key={`content-${category}`}
                    id={`content-${category}`}
                    role="tabpanel"
                    aria-labelledby={`tab-${category}`}
                    tabIndex={0}
                    hidden={currentCategory !== category}
                    ref={(element) => {
                      tabsContentRef.current[index] = element
                    }}
                    className={styles['tab-content']}
                  >
                    <ProductsSummary
                      {...{ customerType, extraStartSimulationAction, platformCostReduction }}
                      region={currentRegion}
                      products={visibleUsableProducts}
                      openDrawer={(product) => {
                        setDrawerProduct(product)
                        setDetailsDrawerOpen(true)
                      }}
                    />
                  </div>
                ))
              ) : (
                <ProductsSummary
                  {...{ customerType, extraStartSimulationAction, platformCostReduction }}
                  region={currentRegion}
                  products={visibleUsableProducts}
                  openDrawer={(product) => {
                    setDrawerProduct(product)
                    setDetailsDrawerOpen(true)
                  }}
                />
              )}

              {(!showTabs || currentCategory === SELECTABLE_CATEGORIES.plenty) && (
                <Card as="section" color="soft" className={styles['plenty-card']}>
                  <Heading as="h2" variant="h6">
                    {t('comparison.plenty.title')}
                  </Heading>

                  <ul>
                    {(
                      t('comparison.plenty.bullets', {
                        returnObjects: true,
                        context: currentRegion !== REGIONS.flanders ? 'notFlanders' : 'flanders'
                      }) as string[]
                    ).map((bullet, index) => (
                      <li key={`plenty-${index}`}>{parse(bullet)}</li>
                    ))}
                  </ul>

                  <Link href={PLENTY_URL} as="a" target="_blank">
                    {t('comparison.plenty.action')}
                  </Link>

                  <Shape color="on-soft" theme="plenty" shape="s5" className={styles.shape} />
                </Card>
              )}
            </Card>
          </div>
        </div>
      </Container>

      {/* PRODUCT DETAIL DRAWER */}
      <ProductDrawer
        {...{ cmsProducts, extraStartSimulationAction, platformCostReduction }}
        isOpen={detailsDrawerOpen}
        onRequestClose={() => setDetailsDrawerOpen(false)}
        pricelists={
          drawerProduct
            ? {
                electricity: apiProducts?.electricity?.[EL_PRODUCT_CODES?.[drawerProduct]]?.pricelists?.[customerType]?.[locale],
                electricityCounterpart:
                  apiProducts?.electricity?.[EL_PRODUCT_CODES?.[PRODUCTS_CONFIG[drawerProduct]?.[PRODUCT_CONFIG_OPTIONS.counterpart]]]
                    ?.pricelists?.[customerType]?.[locale],
                gas: apiProducts?.gas?.[GAS_PRODUCT_CODES?.[drawerProduct]]?.pricelists?.[customerType]?.[locale],
                gasCounterpart:
                  apiProducts?.gas?.[GAS_PRODUCT_CODES?.[PRODUCTS_CONFIG[drawerProduct]?.[PRODUCT_CONFIG_OPTIONS.counterpart]]]
                    ?.pricelists?.[customerType]?.[locale]
              }
            : undefined
        }
        product={drawerProduct}
        userType={customerType}
      />
    </>
  )
}

export default ProductsSummarySection
